import Disclosure from 'core/components/disclosure/disclosure';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import React, { useState, useEffect } from 'react';

import { Grid } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';

import { policyType as PolicyType } from '@ourbranch/lookups';

import { Card } from 'core/components/card';
import Section from 'core/components/section';
import { useStore } from 'core/store';
import caret from 'core/assets/svg/caretOrange.svg';
import useSession from 'core/hooks/use-session';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { track } from 'core/helpers/analytics';
import { getTotalPrice } from 'core/helpers/formatters';
import { COMPARISON_MODAL_OPEN_PARAM } from 'core/helpers/constants';

import FeeBreakdown from '../fee-breakdown';
import PriceBreakdown from '../price-breakdown';
import EquityBreakdown from '../equity-breakdown';
import PriceSummary from '../price-summary';
import useStyles from './price-details.styles';
import NotificationIconWhite from 'core/assets/svg/alert-on-card.svg';
import RenewalCompareIcon from 'core/assets/svg/ic-renewal.svg';
import CompareRenewalPriceFactorsModal from './compare-renewal-price-factors-modal';

const priceChangeVerbFor = (priceChange) => {
  if (priceChange > 0) {
    return 'increased';
  }
  return priceChange < 0 ? 'decreased' : 'remained the same';
};

function PriceDetails() {
  const store = useStore();
  const policyStore = store.account.policies.policy;
  const policiesList = store.account.policies.list;
  const { account } = store;
  const { billingDetails, writeOffTransactions, policy: currentPolicy, getRenewalPriceComparison } = policyStore;
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [compareModalOpen, setCompareModalOpen] = useState(
    () => new URLSearchParams(window.location.search).get(COMPARISON_MODAL_OPEN_PARAM) === 'true'
  );
  const { isExternalSales, isAgency } = useSession();
  const [priceComparison, setPriceComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(true);
  const [comparisonError, setComparisonError] = useState(false);

  const previousPolicy = policiesList
    .filter(
      (policy) =>
        // is it not the current policy
        policy.id !== currentPolicy.id &&
        // ensure that it matches and that it is not a home2 policy etc
        policy.id.slice(0, 15) === currentPolicy.id.slice(0, 15) &&
        // ensure policy types match
        policy.policyType === currentPolicy.policyType &&
        // ensure policies are active or inactive
        (getPolicyStatus(policy) === PolicyStatus.Inactive || getPolicyStatus(policy) === PolicyStatus.Active)
    )
    .sort((a, b) => b.term - a.term)?.[0];

  const priceComparisonModalAvailable =
    (currentPolicy?.policyType === PolicyType.Auto || currentPolicy?.policyType === PolicyType.Home) &&
    currentPolicy.term > 1;

  // Fetched on modal open, not on tab mount.
  useEffect(() => {
    const fetchComparison = async () => {
      setLoadingComparison(true);
      const { ok, renewalPriceComparison } = await getRenewalPriceComparison(currentPolicy.id, previousPolicy.id);
      setPriceComparison(ok ? renewalPriceComparison : null);
      setComparisonError(!ok);
      setLoadingComparison(false);
    };

    if (compareModalOpen && previousPolicy && priceComparisonModalAvailable) {
      fetchComparison();
    }
  }, [policyStore, compareModalOpen, previousPolicy?.id, currentPolicy?.id, priceComparisonModalAvailable]);

  const totalPriceFor = (policy) =>
    getTotalPrice(policy?.premium || 0, policy?.fees || [], policy?.surplusContribution || 0);

  const policyPriceChange = Math.round((totalPriceFor(currentPolicy) - totalPriceFor(previousPolicy)) * 100) / 100;
  const priceChangeVerb = priceChangeVerbFor(policyPriceChange);

  const showCompareModal = compareModalOpen && Boolean(previousPolicy) && priceComparisonModalAvailable;

  useEffect(() => {
    if (showCompareModal) {
      track('Viewed Renewal Price Increase Modal', {
        policyID: currentPolicy.id,
        priceIncrease: policyPriceChange,
        policyType: currentPolicy.policyType,
        isAgency,
        memberAccountId: account.id
      });
    }
  }, [showCompareModal]);

  const toggleCompareModal = () => {
    const url = new URL(window.location.href);
    if (!compareModalOpen) {
      url.searchParams.set(COMPARISON_MODAL_OPEN_PARAM, 'true');
    } else {
      url.searchParams.delete(COMPARISON_MODAL_OPEN_PARAM);
    }
    window.history.pushState({}, '', url);
    setCompareModalOpen(!compareModalOpen);
  };

  return (
    <Section title="Price Details" type="SubSection">
      {showCompareModal && (
        <CompareRenewalPriceFactorsModal
          open={compareModalOpen}
          onClose={toggleCompareModal}
          comparison={priceComparison}
          loading={loadingComparison}
          error={comparisonError}
        />
      )}
      <Card type="secondary" className={classes.priceCard}>
        <Disclosure open={open} collapsedSize={100}>
          {(contentMounted) => (
            <Card className={classes.container}>
              <Grid className={classes.cardHeader}>
                <PriceSummary
                  billingDetails={billingDetails}
                  writeOffTransactions={writeOffTransactions}
                  premium={policyStore.premium}
                  fees={policyStore.totalFeeCost}
                  surplus={policyStore.surplusContribution}
                />
                <Button onClick={() => setOpen(!open)} variant="text" color="secondary">
                  {!open ? 'See Breakdown' : 'Close Breakdown'}{' '}
                  {
                    <img
                      className={open ? classes.icon : classes.iconFold}
                      src={caret}
                      alt={open ? 'fold' : 'unfold'}
                    />
                  }
                </Button>
              </Grid>
              {contentMounted && (
                <div inert={!open ? '' : undefined}>
                  <div className={classes.subSection}>
                    <PriceBreakdown
                      premium={policyStore.premium}
                      fees={policyStore.totalFeeCost}
                      surplus={policyStore.surplusContribution}
                    />
                  </div>
                  <div className={classes.subSection}>
                    <FeeBreakdown />
                  </div>
                </div>
              )}
            </Card>
          )}
        </Disclosure>
        {!isExternalSales && (
          <Card type="noBorders" className={classes.container}>
            <EquityBreakdown />
          </Card>
        )}
        {previousPolicy && priceComparisonModalAvailable && (
          <div className={classNames(classes.module, { [classes.moduleCollapsed]: !open })}>
            <div className={classes.priceIncrease}>
              <div className={classes.iconSection}>
                <img className={classes.notificationIcon} src={NotificationIconWhite} alt="Notification Icon" />
                {`This policy's price has ${priceChangeVerb} from the prior term.`}
              </div>
              <IconButton onClick={toggleCompareModal} className={classes.compareSection}>
                <div className={classes.compareLabel}>
                  <img className={classes.compareIcon} src={RenewalCompareIcon} alt="Compare Icon" />
                  Compare
                </div>
              </IconButton>
            </div>
          </div>
        )}
      </Card>
    </Section>
  );
}

export default observer(PriceDetails);
